import type { NextPage } from 'next'
import { useState } from 'react'

import { PageLayout } from 'src/libs/layout'
import { PricingSection } from 'src/libs/page-pricing'
import {
    ClosingSection,
    FaqSection,
    IntroSectionSimple,
    RequestDemoModal,
} from 'src/libs/ui-shared'

const PricingPage: NextPage = () => {
    const [requestDemoLocation, setRequestDemoLocation] = useState('')

    return (
        <PageLayout>
            <IntroSectionSimple title="Pricing" />
            {requestDemoLocation && (
                <RequestDemoModal
                    location={requestDemoLocation}
                    unmount={() => setRequestDemoLocation('')}
                />
            )}
            <div className="pt-12 grid gap-40">
                <PricingSection
                    plans={[
                        {
                            key: 'basic',
                            title: 'Basic',
                            price: '$0',
                            priceDescription: 'No credit card needed.',
                            description:
                                'For indivduals trying out Laserfocus for an unlimited period.',
                            ctaButton: {
                                label: 'Sign up for free',
                                labelShort: 'Sign up now',
                                href: 'https://app.laserfocus.io/signup',
                            },
                        },
                        {
                            isMostPopular: true,
                            key: 'pro',
                            title: 'Pro',
                            price: '$30',
                            priceDescription: 'per seat/month',
                            priceShort: '$30 per seat/mo',
                            planTrial: '30-day free trial',
                            description:
                                'For sales pros who think their time is worth more than $1 per day.',
                            ctaButton: {
                                label: 'Try for free',
                                href: 'https://app.laserfocus.io/signup',
                            },
                        },
                        {
                            key: 'team',
                            title: 'Team',
                            price: 'Custom pricing',
                            description: 'Run your whole sales process on autopilot. ',
                            ctaButton: {
                                label: 'Contact sales',
                                onClick: () => setRequestDemoLocation('Team plan on pricing page'),
                            },
                        },
                    ]}
                    featureBundles={[
                        {
                            title: 'Save time with an enjoyable Salesforce interface',
                            features: [
                                {
                                    title: 'Inline edit anything in real-time',
                                    planValues: {
                                        basic: '✓',
                                        pro: '✓',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Manage Salesforce tasks from anywhere',
                                    planValues: {
                                        basic: '✓',
                                        pro: '✓',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Create / convert Salesforce records',
                                    planValues: {
                                        basic: '✓',
                                        pro: '✓',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Complete activity history',
                                    planValues: {
                                        basic: '✓',
                                        pro: '✓',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Daily reminders plus witty quotes',
                                    planValues: {
                                        basic: '✓',
                                        pro: '✓',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Custom stacks to manage Salesforce',
                                    planValues: {
                                        basic: '2',
                                        pro: 'Unlimited',
                                        team: 'Unlimited',
                                    },
                                },
                                {
                                    title: 'Import views from Salesforce',
                                    planValues: {
                                        basic: '2',
                                        pro: 'Unlimited',
                                        team: 'Unlimited',
                                    },
                                },
                                {
                                    title: 'Schedule your stacks',
                                    planValues: {
                                        basic: '2',
                                        pro: 'Unlimited',
                                        team: 'Unlimited',
                                    },
                                },
                                {
                                    title: 'Note Templates',
                                    planValues: {
                                        basic: '2',
                                        pro: 'Unlimited',
                                        team: 'Unlimited',
                                    },
                                },
                                {
                                    title: 'Custom field groups',
                                    planValues: {
                                        basic: '3',
                                        pro: 'Unlimited',
                                        team: 'Unlimited',
                                    },
                                },
                                {
                                    title: 'Bulk Edit',
                                    planValues: {
                                        basic: '3',
                                        pro: 'Unlimited',
                                        team: 'Unlimited',
                                    },
                                },
                            ],
                        },
                        {
                            title: 'Integrate with your daily tools',
                            features: [
                                {
                                    title: 'Calendar sync',
                                    planValues: {
                                        basic: '✓',
                                        pro: '✓',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Email sync',
                                    planValues: {
                                        basic: '✓',
                                        pro: '✓',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Native phone integration',
                                    planValues: {
                                        basic: '–',
                                        pro: '–',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Salesloft/Outreach integration',
                                    planValues: {
                                        basic: '–',
                                        pro: '–',
                                        team: '✓',
                                    },
                                },
                            ],
                        },
                        {
                            title: 'Run your sales process on autopilot',
                            features: [
                                {
                                    title: 'Role-based processes',
                                    planValues: {
                                        basic: '–',
                                        pro: '–',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Org stacks',
                                    planValues: {
                                        basic: '–',
                                        pro: '–',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Org note templates',
                                    planValues: {
                                        basic: '–',
                                        pro: '–',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Org field groups',
                                    planValues: {
                                        basic: '–',
                                        pro: '–',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Automated workflow',
                                    planValues: {
                                        basic: '–',
                                        pro: '–',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Custom object integration',
                                    planValues: {
                                        basic: '–',
                                        pro: '–',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Signal-based tasks',
                                    planValues: {
                                        basic: '–',
                                        pro: '–',
                                        team: '✓',
                                    },
                                },
                            ],
                        },
                        {
                            title: 'Premium Support',
                            features: [
                                {
                                    title: 'Live Chat Support',
                                    planValues: {
                                        basic: '✓',
                                        pro: '✓',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Email Support',
                                    planValues: {
                                        basic: '✓',
                                        pro: '✓',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Shared Slack channel',
                                    planValues: {
                                        basic: '–',
                                        pro: '✓',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Video Call Support',
                                    planValues: {
                                        basic: '–',
                                        pro: '✓',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Individual Onboarding and Training',
                                    planValues: {
                                        basic: '–',
                                        pro: '✓',
                                        team: '✓',
                                    },
                                },
                                {
                                    title: 'Dedicated Customer Success Manager',
                                    planValues: {
                                        basic: '–',
                                        pro: '–',
                                        team: '✓',
                                    },
                                },
                            ],
                        },
                    ]}
                />

                <FaqSection
                    title="Frequently asked questions"
                    description="Laserfocus helps you become more productive by offering you all the tools you need to close deals with confidence."
                    elements={[
                        {
                            question: 'How can I try Laserfocus for myself?',
                            answer: 'Connect with one click via Salesforce Oauth and start using Laserfocus. Laserfocus sits on top of your Salesforce data, so there is no setup or installation required.',
                        },
                        {
                            question: 'Do I need my Salesforce Admin for setup?',
                            answer: [
                                'Nope. Laserfocus doesn’t require any setup or installation.',
                                'You securely log in and connect via Salesforce Oauth with Laserfocus and can start using it instantly if your Salesforce license has API access.',
                            ],
                        },
                        {
                            question: 'How long does the setup need?',
                            answer: 'Like everything in Laserfocus: it’s superfast. One click and you are ready. Means 30 seconds.',
                        },
                        {
                            question: 'Are you changing my Salesforce setup?',
                            answer: [
                                'No. Salesforce is defined and setup by your company, we don’t interact with your workflows in Salesforce or create any fields.',
                                'Everything stays untouched in your Salesforce instance.',
                            ],
                        },
                        {
                            question: 'How much time can Laserfocus save me?',
                            answer: 'Glad you asked. Just think about how much time you spend in Salesforce per day and cut that by 80%.',
                        },
                    ]}
                />

                <ClosingSection
                    title="Streamline your GTM now"
                    description="Don’t let complex processes and tools slow you down."
                />
            </div>
        </PageLayout>
    )
}

export default PricingPage
